import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { FaSearch, FaUserShield, FaUsers } from "react-icons/fa";
import useApi from "../../hooks/useApi";
import { DEFAULT_ERROR, GET_BUSINESS_MANAGERS_URL } from "../../api/urls";
import {
  ActionButton,
  Avatar,
  EmptyState,
  ManagerBadge,
  ManagerCount,
  MemberEmail,
  MemberInfo,
  MemberList,
  MemberName,
  MemberRow,
  ModalIntro,
  SearchInput,
  SearchWrap,
} from "./ManagerModalStyles";

const userId = (user) => user?.id ?? user?.userId ?? user?._id;

const displayName = (user = {}) =>
  user.name || [user.firstname, user.lastname].filter(Boolean).join(" ") || "Channel member";

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

export const ManagerModal = ({ show, onHide, isOwner }) => {
  const api = useApi();
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [serverOwner, setServerOwner] = useState(isOwner);

  const loadMembers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(GET_BUSINESS_MANAGERS_URL);
      const data = response.data || {};
      const subscribers = Array.isArray(data)
        ? data
        : data.subscribers || data.users || data.payload || [];
      const managerIds = new Set(
        (data.managers || []).map((manager) => String(userId(manager)))
      );
      setMembers(
        subscribers.map((member) => ({
          ...member,
          isManager:
            Boolean(member.isManager ?? member.is_manager) ||
            managerIds.has(String(userId(member))),
        }))
      );
      if (typeof data.isOwner === "boolean") setServerOwner(data.isOwner);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Could not load channel members");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (show) {
      setSearch("");
      loadMembers();
    }
  }, [loadMembers, show]);

  const visibleMembers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return members;
    return members.filter((member) =>
      [displayName(member), member.email, member.schoolName]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [members, search]);

  const managerCount = members.filter((member) => member.isManager).length;
  const canManage = Boolean(isOwner && serverOwner);

  const toggleManager = async (member) => {
    if (!canManage) return;
    const id = userId(member);
    try {
      setActionId(id);
      if (member.isManager) {
        await api.delete(`${GET_BUSINESS_MANAGERS_URL}/${id}`);
      } else {
        await api.post(GET_BUSINESS_MANAGERS_URL, { userId: id });
      }
      setMembers((current) =>
        current.map((item) =>
          String(userId(item)) === String(id)
            ? { ...item, isManager: !member.isManager }
            : item
        )
      );
      toast.success(member.isManager ? "Manager removed" : "Manager added");
    } catch (error) {
      toast.error(error?.response?.data?.error || DEFAULT_ERROR);
    } finally {
      setActionId(null);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontFamily: "rebondG-Bold", fontSize: 19 }}>
          <FaUserShield color="#2f67d8" style={{ marginRight: 9 }} />
          Channel managers
          <ManagerCount>{managerCount}</ManagerCount>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: 20 }}>
        <ModalIntro>
          Promote subscribed learners to help manage this channel, or remove existing
          managers. Only the channel owner can make these changes.
        </ModalIntro>

        <SearchWrap>
          <FaSearch size={13} />
          <SearchInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search subscribed users"
            aria-label="Search subscribed users"
          />
        </SearchWrap>

        {loading ? (
          <EmptyState>Loading subscribed users…</EmptyState>
        ) : visibleMembers.length ? (
          <MemberList>
            {visibleMembers.map((member) => {
              const id = userId(member);
              return (
                <MemberRow key={id}>
                  <Avatar>
                    {member.img || member.profile_picture ? (
                      <img src={member.img || member.profile_picture} alt="" />
                    ) : (
                      initials(displayName(member) || member.email)
                    )}
                  </Avatar>
                  <MemberInfo>
                    <MemberName>{displayName(member)}</MemberName>
                    <MemberEmail>{member.email}</MemberEmail>
                  </MemberInfo>
                  {member.isManager && <ManagerBadge>Manager</ManagerBadge>}
                  <ActionButton
                    type="button"
                    $remove={member.isManager}
                    disabled={!canManage || String(actionId) === String(id)}
                    onClick={() => toggleManager(member)}
                  >
                    {String(actionId) === String(id)
                      ? "Saving…"
                      : member.isManager
                      ? "Remove"
                      : "Add"}
                  </ActionButton>
                </MemberRow>
              );
            })}
          </MemberList>
        ) : (
          <EmptyState>
            <FaUsers size={28} />
            {search ? "No subscribed users match your search" : "No subscribed users found"}
          </EmptyState>
        )}
      </Modal.Body>
    </Modal>
  );
};
